import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Dropdown, Button } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import ResultPage from '@/container/ResultPage';

import { useGetAllOrganisersListQuery, useGetProfileQuery, useGetAllEventsListQuery, useGetAllExhibitorsListQuery, useGetCarriersQuery } from '../services/service';
import { clearAuth } from '@/utils/authSession';

import { usePagePreloader } from '@/hooks/useLazyLoad';

import useSupportiveDataStore from '@/store/supportiveData';
import { manualEnquiryLeaveGuardStore } from '@/store/manualEnquiryLeaveGuardStore';

import {
  IconTeam,
  IconUser,
  IconEvent,
  IconFileFa,
  IconWarehouse,
  IconCode,
  IconDashboard,
  IconManifest,
  IconLogout,
  IconMenuFold,
  IconMenuUnfold
} from '@/utils/icons';

import { LOGO_AND_NAME_IMG, USER_PROFILE_IMG } from '@/utils/image';

import { exhibitorListToOptions, organiserListToOptions, eventListToOptions, logHelper } from '@/utils';

const { Header, Sider, Content } = Layout;

const MANUAL_ENQUIRY_PATH = '/admin/manual-enquiry';

const TAG = "AdminLayout: ";
const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preloadPage } = usePagePreloader();

  const { organisersList, setOrganisersList, eventsList, setEventsList, exhibitorsList, setExhibitorsList } = useSupportiveDataStore();

  useGetCarriersQuery();

  const [collapsed, setCollapsed] = useState(false);
  const [isError, setIsError] = useState<boolean | string>(false);

  const navigateToMenuKey = (navigationKey: string) => {

    const pageName = navigationKey === '/' ? 'home' : navigationKey.slice(1);

    preloadPage(pageName);

    const runNavigate = () => {
      if (navigationKey === '/admin/new-enquiry' && location.pathname === '/admin/new-enquiry') {
        navigate(`${navigationKey}?refresh=${Date.now()}`);
        return;
      }
      if (navigationKey === '/admin/manual-enquiry' && location.pathname === '/admin/manual-enquiry') {
        navigate(`${navigationKey}?refresh=${Date.now()}`);
        return;
      }
      navigate(navigationKey);
    };

    if (
      location.pathname === MANUAL_ENQUIRY_PATH &&
      manualEnquiryLeaveGuardStore.getState().shouldBlock
    ) {
      manualEnquiryLeaveGuardStore.getState().requestLeaveConfirmation((confirmed) => {
        if (confirmed) runNavigate();
      });
      return;
    }

    runNavigate();
  };

  const menuItems = [
    // {
    //   key: '/admin/dashboard',
    //   icon: <IconDashboard />,
    //   label: 'Dashboard',
    // },
    // {
    //   key: '/admin/event-show-dashboard',
    //   icon: <IconEvent />,
    //   label: 'Event dashboard',
    // },
    // {
    //   key: '/admin/fm-dashboard',
    //   icon: <IconDashboard />,
    //   label: 'FM Dashboard',
    //   children: [
    //     { key: '/admin/fm-dashboard/metrics', label: 'Metrics' },
    //     { key: '/admin/fm-dashboard/operations', label: 'Operations' },
    //     { key: '/admin/fm-dashboard/shows', label: 'Shows' },
    //     { key: '/admin/fm-dashboard/returns', label: 'Returns' },
    //     { key: '/admin/fm-dashboard/carriers', label: 'Carriers' },
    //     { key: '/admin/fm-dashboard/admin', label: 'Admin' },
    //   ],
    // },
    // {
    //   key: '/admin/feedbacks',
    //   icon: <IconFeedback />,
    //   label: 'Feedbacks',
    // },
    {
      key: '/admin/organisers',
      icon: <IconTeam />,
      label: 'Organisers',
    },
    {
      key: '/admin/events',
      icon: <IconEvent />,
      label: 'Events',
    },
    {
      key: '/admin/enquiries',
      icon: <IconFileFa />,
      label: 'Enquiries',
    },
    {
      key: '/admin/warehouses',
      icon: <IconWarehouse />,
      label: 'Warehouses',
    },
    {
      key: '/admin/users',
      icon: <IconUser />,
      label: 'Users',
    },
    {
      key: '/admin/program-management',
      icon: <IconCode />,
      label: 'Program Management',
    },
    // {
    //   key: '/admin/new-enquiry',
    //   icon: <IconFileFa />,
    //   label: 'New Enquiry',
    // },
    // {
    //   key: '/admin/manifest',
    //   icon: <IconManifest />,
    //   label: 'Manifest',
    // },
    // {
    //   key: '/admin/manual-enquiry',
    //   icon: <IconFileFa />,
    //   label: 'Enquiry By Admin',
    // },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    const navigationKey = key === '/admin/fm-dashboard' ? '/admin/fm-dashboard/metrics' : key;
    navigateToMenuKey(navigationKey);
  };

  const handleLogout = () => {
    clearAuth();
  };

  const {
    data: profileData,
  } = useGetProfileQuery();


  const {
    data: organisersListAll,
    isFetching: isFetchingOrganisersList,
    isError: isErrorOrganisersList,
  } = useGetAllOrganisersListQuery(organisersList.length === 0);


  const {
    data: eventsListAll,
    isFetching: isFetchingEventsList,
    isError: isErrorEventsList,
  } = useGetAllEventsListQuery(eventsList?.length === 0);

  const {
    data: exhibitorsListAll,
    isFetching: isFetchingExhibitorsList,
    isError: isErrorExhibitorsList,
  } = useGetAllExhibitorsListQuery(exhibitorsList?.length === 0);


  useEffect(() => {
    if (isFetchingOrganisersList == false && !isErrorOrganisersList) {

      if (organisersListAll?.status !== 200 || organisersListAll?.data?.statusCode !== 200 || organisersListAll?.data?.status !== "success") {
        setIsError(organisersListAll?.data?.errorMessage || "Something went wrong");
        return;
      }

      setOrganisersList(organiserListToOptions(organisersListAll?.data?.data || []));

    }
  }, [isFetchingOrganisersList, isErrorOrganisersList, organisersListAll, setOrganisersList]);

  useEffect(() => {
    if (isFetchingEventsList == false && !isErrorEventsList) {

      if (eventsListAll?.status !== 200 || eventsListAll?.data?.statusCode !== 200 || eventsListAll?.data?.status !== "success") {
        setIsError(eventsListAll?.data?.errorMessage || "Something went wrong");
        return;
      }

      setEventsList(eventListToOptions(eventsListAll?.data?.data || []));

    }
  }, [isFetchingEventsList, isErrorEventsList, eventsListAll, setEventsList]);

  useEffect(() => {
    if (isFetchingExhibitorsList == false && !isErrorExhibitorsList) {

      if (exhibitorsListAll?.status !== 200 || exhibitorsListAll?.data?.statusCode !== 200 || exhibitorsListAll?.data?.status !== "success") {
        setIsError(exhibitorsListAll?.data?.errorMessage || "Something went wrong");
        return;
      }

      const exhibitorsRawData = Array.isArray(exhibitorsListAll?.data?.data)
        ? exhibitorsListAll?.data?.data
        : exhibitorsListAll?.data?.data?.results || [];

      setExhibitorsList(exhibitorListToOptions(exhibitorsRawData));

    }
  }, [isFetchingExhibitorsList, isErrorExhibitorsList, exhibitorsListAll, setExhibitorsList]);

  const userMenuItems = [
    // {
    //   key: 'profile',
    //   label: 'Profile',
    //   icon: <IconUser />,
    //   onClick: () => {
    //     preloadPage('settings');
    //     navigate('/settings');
    //   },
    // },
    {
      key: 'logout',
      label: 'Logout',
      icon: <IconLogout />,
      onClick: handleLogout,
    },
  ];

  const selectedMenuKey = useMemo(() => {
    if (location.pathname === '/admin/fm-dashboard') return '/admin/fm-dashboard/metrics';
    return location.pathname;
  }, [location.pathname]);

  const fmMenuOpenKeys = useMemo(() => ['/admin/fm-dashboard'], []);

  // logHelper(TAG, ' profileData ', profileData?.data?.data);

  if (isError !== false) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <ResultPage
          status="error"
          title="Something went wrong"
          subTitle={JSON.stringify(isError)}
        />
      </Suspense>
    );
  }

  return (
    <Layout className="min-h-screen !font-montserrat">

      <Sider
        trigger={null}
        collapsible
        // collapsed={collapsed}
        collapsed={collapsed}
        breakpoint="md"
        collapsedWidth="0"
        onBreakpoint={(broken) => {
          if (broken) {
            setCollapsed(true);
          } else {
            // When screen goes back to desktop/tablet width,
            // restore sidebar visibility without requiring refresh.
            setCollapsed(false);
          }
        }}
        className="bg-white"
      >
        <div className={`h-8 m-4 bg-white/20 rounded-lg flex items-center justify-center font-bold ${collapsed ? 'text-xs' : 'text-base'}`}>
          {collapsed ? 'SITE' : 'SITE'}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          defaultOpenKeys={fmMenuOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      <Layout className=" !bg-[#F0F8FF]  ">

        <Header className="px-4 bg-white flex items-center relative justify-center !h-[80px] shadow-[0_0_8px_8px_#00000021] ">

          <div className=" absolute left-[1rem] flex items-center gap-2 ">

            <Button
              type="text"
              icon={collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              onClick={() => setCollapsed(!collapsed)}
              className="text-base w-16 h-16 md:hidden"
            />

            <img
              src={LOGO_AND_NAME_IMG}
              alt="logo"
              className="h-auto w-[160px] xs:w-[220px] sm:w-[160px] md:w-[300px] min-w-0"
              loading="lazy"
            />
          </div>

          <div className=" absolute right-[1rem] hidden md:block ">
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >

              <div className="rounded-[22px] h-[40px] flex items-center bg-white p-1 border-[1px] border-solid border-[#D3D3D3] w-[200px]">

                <div className="w-[35px] h-[35px] flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src={USER_PROFILE_IMG}
                    alt="logo"
                    className=" w-[35px] h-[35px] rounded-[50%] object-cover object-center "
                    loading="lazy"
                  />
                </div>

                <div className="ml-2 flex-1 h-full flex items-center w-[120px] ">
                  <p className="text-[12px] text-[#000000] uppercase truncate shrink-0 w-full ">
                    {profileData?.data?.data?.company_name || "_"}
                  </p>
                </div>

                <div className="h-full flex items-center justify-center mr-2">
                  <svg className="w-[13px] h-auto " width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L8 8.5L15 1.5" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

              </div>
            </Dropdown>
          </div>

          <div className=" absolute right-[1rem] md:hidden ">
            <Button
              type="text"
              icon={<IconLogout />}
              onClick={handleLogout}
              className="text-base w-16 h-16 md:hidden"
            />
          </div>

        </Header>

        <Content className="m-0 p-3 md:p-6 min-h-[280px] overflow-y-auto overflow-x-hidden bg-[#F0F8FF] !h-[calc(100vh_-_80px)] max-h-[calc(100vh_-_80px)]">
          <Outlet />
        </Content>

      </Layout>
    </Layout>
  );
};

export default AdminLayout; 